import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import Send from '@mui/icons-material/Send';
import { SxProps, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import { t } from 'i18next';
import { debounce } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import useDriverSchedulerFetch from '~pages/Dispatch/hooks/useDriverSchedulerFetch';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import CreateOrderButton from '../CreateOrderButton';
import { SendAllJobsForDayDialog } from '../SendAllJobsForDayDialog';

const searchBarMaxWidthInPx = 180;

interface DriverSchedulerFiltersBarProps {
  isOrderFormDrawerMinimized?: boolean;
  onNewOrderClick: () => void;
}

export const DriverSchedulerFiltersBar = observer(
  ({ isOrderFormDrawerMinimized, onNewOrderClick }: DriverSchedulerFiltersBarProps) => {
    const { bulkSendDayJobs, getBulkSendDayJobsCount } = useDriverSchedulerFetch();
    const { driverSchedulerStore, userStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);
    const [isSendAllJobsDialogOpen, setIsSendAllJobsDialogOpen] = useState(false);

    // The count and the send read the same live filters: a filter change while the dialog is
    // open re-creates the count callback, which makes the dialog discard its count and refetch.
    const sendAllJobsDate = driverSchedulerStore.dateFilters.startDate;
    const { assignedJobsFilters, assigneesFilters } = driverSchedulerStore;

    const fetchSendAllJobsCount = useCallback(() => {
      if (!sendAllJobsDate) {
        return Promise.resolve(0);
      }

      return getBulkSendDayJobsCount({
        assignedJobsFilters,
        assigneesFilters,
        companyId,
        date: sendAllJobsDate,
      });
    }, [
      assignedJobsFilters,
      assigneesFilters,
      companyId,
      getBulkSendDayJobsCount,
      sendAllJobsDate,
    ]);

    const handleSendAllJobs = useCallback(async () => {
      if (!sendAllJobsDate) {
        return;
      }

      const sentJobIds = await bulkSendDayJobs({
        assignedJobsFilters,
        assigneesFilters,
        companyId,
        date: sendAllJobsDate,
      });
      AnalyticsController.track('send_all_jobs_submitted', {
        view: 'drivers',
        job_count: sentJobIds.length,
      });
      setIsSendAllJobsDialogOpen(false);
    }, [
      assignedJobsFilters,
      assigneesFilters,
      bulkSendDayJobs,
      companyId,
      sendAllJobsDate,
    ]);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
          }}
        >
          <SendAllJobsButton
            onClick={() => {
              AnalyticsController.track('send_all_jobs_opened', { view: 'drivers' });
              setIsSendAllJobsDialogOpen(true);
            }}
            disabled={userStore.isViewOnlyUser}
          />

          <CreateOrderButton
            isForResumeOrderCreation={isOrderFormDrawerMinimized}
            disabled={userStore.isViewOnlyUser}
            onClick={onNewOrderClick}
          />
        </Box>

        <SendAllJobsForDayDialog
          fetchJobsCount={fetchSendAllJobsCount}
          isOpen={isSendAllJobsDialogOpen}
          onClose={() => setIsSendAllJobsDialogOpen(false)}
          onConfirm={handleSendAllJobs}
        />
      </Box>
    );
  },
);

interface SearchBarProps {
  isAlwaysExpanded?: boolean;
  sx?: SxProps;
}

export const SearchBar = observer(({ isAlwaysExpanded, sx }: SearchBarProps) => {
  const theme = useTheme();
  const { driverSchedulerStore } = useStores();
  const inputRef = useRef<HTMLInputElement>();
  const [isExpanded, setIsExpanded] = useState(Boolean(isAlwaysExpanded));
  const [inputValue, setInputValue] = useState('');

  const inputHasValue = inputValue.length > 0;
  const applyFilter = debounce(() => {
    driverSchedulerStore.setAssignedJobsFilters({ search: inputValue }, true);
  }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS);

  useEffect(() => {
    if (!isExpanded) {
      setInputValue('');
    }
  }, [isExpanded]);

  useEffect(() => {
    applyFilter();

    return () => {
      applyFilter.cancel();
    };
  }, [inputValue]);

  return (
    <Box sx={sx}>
      <Collapse
        collapsedSize={`calc(18px + (${theme.spacing(1)} * 2)`}
        in={isExpanded}
        orientation="horizontal"
        sx={{ '& .MuiCollapse-wrapperInner': { width: '100%' } }}
      >
        <Box
          onClick={() => {
            if (!isAlwaysExpanded) {
              setIsExpanded(true);
              inputRef.current?.focus();
            }
          }}
          data-ui="page-dispatch__search-bar__button"
          sx={{
            border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
            borderRadius: theme.brandV2.borderRadius,
            maxWidth: searchBarMaxWidthInPx,
            width: '100%',
          }}
        >
          <OutlinedInput
            inputRef={inputRef}
            fullWidth
            onBlur={() => {
              if (!isAlwaysExpanded && !inputHasValue) {
                setIsExpanded(false);
              }
            }}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={`${t('dispatch.drivers.search')}`}
            size="small"
            value={inputValue}
            startAdornment={
              <InputAdornment position="start">
                <Search
                  sx={{
                    color: theme.brandV2.colors.treadBlack,
                    cursor: 'pointer',
                    fontSize: '16px',
                  }}
                />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end" onClick={() => setInputValue('')}>
                <Close
                  sx={{
                    color: theme.brandV2.colors.treadBlack,
                    cursor: 'pointer',
                    fontSize: '16px',
                    visibility: inputHasValue ? 'visible' : 'hidden',
                  }}
                />
              </InputAdornment>
            }
            sx={{
              '&.MuiInputBase-root': {
                height: `calc(${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px - 2px)`,
                px: 1,
                py: 0.5,
                '.MuiInputBase-input': {
                  fontSize: '12px',
                  p: 0,
                },
                '.MuiOutlinedInput-notchedOutline': {
                  border: 0,
                },
              },
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
});

interface SendAllJobsButtonProps {
  onClick: () => void;
  disabled?: boolean;
}

function SendAllJobsButton({ onClick, disabled = false }: SendAllJobsButtonProps) {
  const theme = useTheme();

  return (
    <Box>
      <SmallButton
        onClick={onClick}
        disabled={disabled}
        startIcon={<Send />}
        sx={{
          '&.MuiButtonBase-root': {
            p: `0 ${theme.spacing(1)}`,
            backgroundColor: theme.semantic.surfaceRaised,
            color: theme.palette.text.primary,
            border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
            borderRadius: theme.brandV2.borderRadius,
            height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
          },
        }}
      >
        {t('dispatch.dispatch_v2.send_all_jobs')}
      </SmallButton>
    </Box>
  );
}
